import { CONFIG } from './config.js';
import { areaByPlayer } from './voronoi.js';

const PLAYER_COUNT = CONFIG.players.length;
const TOTAL_TURNS = CONFIG.sitesPerPlayer * PLAYER_COUNT;

/** Playfield rectangle, excluding the HUD strip along the top. */
export const BOUNDS = [0, CONFIG.hudHeight, CONFIG.width, CONFIG.height];

export function createGame() {
  return { sites: [], turn: 0 };
}

export function currentPlayer(game) {
  return game.turn % PLAYER_COUNT;
}

export function isOver(game) {
  return game.turn >= TOTAL_TURNS;
}

export function turnsLeft(game) {
  return TOTAL_TURNS - game.turn;
}

export function inBounds(x, y) {
  const [x0, y0, x1, y1] = BOUNDS;
  return x >= x0 && x <= x1 && y >= y0 && y <= y1;
}

/** A site may only go on empty ground, far enough from every existing site. */
export function canPlace(game, x, y) {
  if (isOver(game) || !inBounds(x, y)) return false;
  return game.sites.every(
    (s) => Math.hypot(s.x - x, s.y - y) >= CONFIG.minSiteDistance,
  );
}

export function placeSite(game, x, y) {
  if (!canPlace(game, x, y)) return false;
  game.sites.push({ x, y, player: currentPlayer(game) });
  game.turn++;
  return true;
}

export function scores(game) {
  return areaByPlayer(game.sites, BOUNDS, PLAYER_COUNT);
}

/**
 * Greedy AI: sample candidate points and keep the one that maximises its own
 * captured area. Cheap, beatable, and a fine baseline to replace.
 */
export function chooseAiMove(game, random = Math.random) {
  const me = currentPlayer(game);
  const [x0, y0, x1, y1] = BOUNDS;
  let best = null;
  let bestArea = -Infinity;

  for (let i = 0; i < CONFIG.aiSamples; i++) {
    const x = x0 + random() * (x1 - x0);
    const y = y0 + random() * (y1 - y0);
    if (!canPlace(game, x, y)) continue;

    const trial = [...game.sites, { x, y, player: me }];
    const area = areaByPlayer(trial, BOUNDS, PLAYER_COUNT)[me];
    if (area > bestArea) {
      bestArea = area;
      best = { x, y };
    }
  }

  return best;
}
